import { formatPaidDay } from '@/lib/campaigns/paid-dates';

let reportingZone: string | undefined;

/**
 * Called once by the browser app at start-up so every page's window also ends where the viewer is.
 * Server code and agent tools never call it: they keep whole UTC days and say when an account's
 * own day is still running.
 */
export function adoptViewerReportingZone(): void {
  try {
    reportingZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    reportingZone = undefined;
  }
}

function calendarDayIn(timeZone: string | undefined, at: Date): string | null {
  if (!timeZone) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(at);
  } catch {
    return null;
  }
}

/**
 * The paid workspace reports only completed days. Provider backfills can still
 * revise recent values, but a partial current day must not be compared with a
 * completed Google, Meta, GA4, or Shopify day. In the app the window ends on
 * yesterday in UTC or yesterday where the viewer is, whichever is earlier: from
 * 8 pm in New York, UTC's yesterday is New York's today, and a US account's day
 * is still running.
 */
export function completedPaidReportingWindow(
  days: number,
  now = new Date(),
  timeZone: string | undefined = reportingZone,
): { since: string; until: string } {
  const utcToday = now.toISOString().slice(0, 10);
  const localToday = calendarDayIn(timeZone, now);
  const today = localToday && localToday < utcToday ? localToday : utcToday;
  const until = new Date(`${today}T00:00:00.000Z`);
  until.setUTCDate(until.getUTCDate() - 1);
  const since = new Date(until);
  since.setUTCDate(since.getUTCDate() - Math.max(1, days) + 1);
  return {
    since: since.toISOString().slice(0, 10),
    until: until.toISOString().slice(0, 10),
  };
}

export function completedPaidReportingDateRange(
  days: number,
  now = new Date(),
  timeZone: string | undefined = reportingZone,
) {
  const window = completedPaidReportingWindow(days, now, timeZone);
  return {
    from: new Date(`${window.since}T00:00:00.000Z`),
    to: new Date(`${window.until}T00:00:00.000Z`),
  };
}

/**
 * The reporting windows the paid workspace offers, everywhere it offers one.
 *
 * `PaidCampaignsPage` had this as a private `DAY_OPTIONS` const while both
 * campaign detail pages hardcoded the same three values inline in their
 * `?days=` guard — so a fourth window could be added to the list page and
 * silently rejected by the detail page it linked to. One list, one place.
 */
// One year is the longest paid window offered in the workspace. It matches
// LinkedIn Ads' initial persisted backfill depth, so an account with seasonal
// delivery can surface its stored history instead of looking permanently empty
// once its last activity is older than 90 days.
export const PAID_REPORTING_DAY_OPTIONS = [7, 30, 90, 365] as const;

export type PaidReportingDays = (typeof PAID_REPORTING_DAY_OPTIONS)[number];

/** Reads a `?days=` param, falling back to 30 for anything we do not offer. */
export function resolvePaidReportingDays(raw: string | number | null): PaidReportingDays {
  const value = Number(raw);
  return PAID_REPORTING_DAY_OPTIONS.includes(value as PaidReportingDays)
    ? (value as PaidReportingDays)
    : 30;
}

/**
 * The equal-length window immediately BEFORE the current one.
 *
 * `completedPaidReportingWindow(30)` returns the last 30 completed days; this
 * returns the 30 completed days before those, with no gap and no overlap. That
 * adjacency is the whole point: a delta is only "vs the previous 30 days" if
 * the comparison window really is the previous 30 days.
 *
 * 🔴 Why this exists at all. Campaign detail fetched ONE window, so every
 * headline number sat there with no comparison — and the tempting shortcut was
 * to split the single window in half and call the second half a trend. That is
 * not "vs the previous period", it is two halves of the same period, and
 * labelling it otherwise would be a fabricated number on the one surface whose
 * entire job is being trustworthy. A real prior window is one more query; an
 * invented one is a lie that looks like a feature.
 */
export function priorPaidReportingWindow(
  days: number,
  now = new Date(),
): { since: string; until: string } {
  const current = completedPaidReportingWindow(days, now);
  const until = new Date(`${current.since}T00:00:00.000Z`);
  until.setUTCDate(until.getUTCDate() - 1);
  const since = new Date(until);
  since.setUTCDate(since.getUTCDate() - Math.max(1, days) + 1);
  return {
    since: since.toISOString().slice(0, 10),
    until: until.toISOString().slice(0, 10),
  };
}

/**
 * The shortest offered window that contains `latestDeliveryDay`, or null when
 * that day is already inside the current window, older than every option, or
 * unknown. Shared by the Google, Meta and LinkedIn detail pages so an empty
 * window offers the one range that shows real delivery instead of leaving the
 * reader to guess which one has any.
 */
export function smallestReportingWindowContaining(
  latestDeliveryDay: string | null | undefined,
  currentDays: number,
  options: readonly number[] = PAID_REPORTING_DAY_OPTIONS,
  now = new Date(),
): number | null {
  if (!latestDeliveryDay) return null;
  const fits = (days: number) => completedPaidReportingWindow(days, now).since <= latestDeliveryDay;
  if (fits(currentDays)) return null;
  return (
    [...options].sort((a, b) => a - b).find((days) => days > currentDays && fits(days)) ?? null
  );
}

/** A UTC calendar day ("2025-11-03") in the paid pages' one date style, never shifted a day by local time. */
export function formatReportingDay(day: string): string {
  return (formatPaidDay(day, { zone: 'utc' }) ?? day).replace(/ /g, NO_BREAK_SPACE);
}

/** A date is one thing: its spaces never break across lines. */
const NO_BREAK_SPACE = '\u00A0';

/**
 * The paid pages say "switched off" for a campaign that is not serving (the title bar reads
 * "Saved, switched off"), never "paused", so one state has one word. Any other status is unchanged.
 */
export function switchedOffStatusLabel(statusLabel: string | null): string | null {
  return statusLabel !== null && statusLabel.trim().toLowerCase() === 'paused'
    ? 'switched off'
    : statusLabel;
}

/**
 * A campaign cannot deliver before it starts, so a reporting window never begins before its start day.
 * Returns the later of the window's first day and `campaignStartDay` (a date or a timestamp, read as its
 * UTC day). Null when the campaign starts after the window ends: there is no day in it the campaign could
 * have run, so no window is worth naming.
 */
export function reportingWindowStartForCampaign(
  startDate: string,
  endDate: string,
  campaignStartDay: string | null | undefined,
): string | null {
  const day =
    campaignStartDay && /^\d{4}-\d{2}-\d{2}/.test(campaignStartDay.trim())
      ? campaignStartDay.trim().slice(0, 10)
      : null;
  const from = day && day > startDate ? day : startDate;
  return from <= endDate ? from : null;
}

/**
 * Why a detail page's window is empty, in the reader's words: the window, the
 * object's own status, and when it last delivered. "No daily metrics are
 * stored" alone read as a broken sync on a campaign that was simply paused.
 * `latestDeliveryDay` undefined means "not known yet" and is left unsaid.
 * `campaignStartDay`, when known, keeps the window from naming days before the campaign could have
 * run; a campaign that starts after the window ends gets no window clause at all. `neverRan` is a campaign
 * Mora saved that was never turned on: its saved start is a draft date, so a window from it would name
 * days it never had a chance to run, and the line leads with the status instead.
 */
export function describeEmptyReportingWindow({
  startDate,
  endDate,
  objectLabel,
  statusLabel,
  latestDeliveryDay,
  campaignStartDay,
  neverRan = false,
}: {
  startDate: string;
  endDate: string;
  objectLabel: string;
  statusLabel: string | null;
  latestDeliveryDay: string | null | undefined;
  campaignStartDay?: string | null;
  neverRan?: boolean;
}): string {
  const from = reportingWindowStartForCampaign(startDate, endDate, campaignStartDay);
  return [
    from === null || neverRan
      ? null
      : `Nothing delivered between ${formatReportingDay(from)} and ${formatReportingDay(endDate)}.`,
    statusLabel ? `This ${objectLabel} is ${statusLabel.toLowerCase()}.` : null,
    latestDeliveryDay === undefined
      ? null
      : latestDeliveryDay
        ? `Its last delivery was ${formatReportingDay(latestDeliveryDay)}.`
        : 'No delivery has been synced for it yet.',
  ]
    .filter(Boolean)
    .join(' ');
}
